'use client';

import React, { useCallback, useMemo } from 'react';
import { ProductDetailView, prepareProductDetailData } from '@org/ui';
import useFavorites from '@/hooks/useFavorites';
import { extractFavoriteData } from '@/lib/favorites';
import { useLanguage } from '@/lib/config/useLanguage';
import { useT } from '@/lib/config/useTranslations';
import { siteConfig } from '@/lib/config/site-config';
import ProductPriceActions from '../../../components/Catalog/ProductPriceActions';

const sanitizeHtmlContent = (html) => {
  if (typeof html !== 'string') return undefined;

  const cleaned = html
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, '')
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, '')
    .replace(/<meta[^>]*>/gi, '')
    .trim();

  return cleaned || undefined;
};

function ProductDetail({ product, detailData: detailDataProp, shortDescription }) {
  const { currency, langCode } = useLanguage();
  
  const detailData = useMemo(() => {
    const prepared = detailDataProp ?? prepareProductDetailData(product, langCode);
    const sanitizedShortDescription = sanitizeHtmlContent(shortDescription);

    // Обновляем shortDescriptionHtml из переданного shortDescription
    if (sanitizedShortDescription) {
      return {
        ...prepared,
        shortDescriptionHtml: sanitizedShortDescription,
      };
    }
    
    return prepared;
  }, [detailDataProp, product, langCode, shortDescription]);

  const { isFavorite, toggleFavorite, isHydrated } = useFavorites();
  const { t } = useT();

  const favoriteBuilder = useCallback(
    ({ product: detail, variation }) => {
      return extractFavoriteData(product, {
        id: variation?.id ?? detail.id ?? detail.code,
        code: detail.code ?? variation?.id,
        name: variation?.name ?? detail.name,
        price: variation?.price ?? detail.price,
        priceOld: variation?.priceOld ?? detail.priceOld,
        picture:
          variation?.picture ??
          detail.picture ??
          (detail.imageGallery.length > 0 ? detail.imageGallery[0] : undefined),
      });
    },
    [product]
  );

  const productDetailConfig = siteConfig.ui?.productDetail;
  const showDescriptionBlock =
    productDetailConfig?.blocks?.description ??
    productDetailConfig?.showDescription ??
    true;
  const showAllSpecificationsBlock =
    productDetailConfig?.blocks?.allSpecifications ??
    productDetailConfig?.showAllSpecifications ??
    true;
  const showWarrantyPaymentBlock =
    productDetailConfig?.showWarrantyPaymentBlock ?? false;
  
  return (
    <ProductDetailView
      product={detailData}
      introText="Подберём исполнение и рассчитаем стоимость под вашу задачу."
      favoriteManager={{
        isHydrated,
        isFavorite,
        toggleFavorite,
        buildEntry: favoriteBuilder,
      }}
      currency={currency}
      translations={{
        addToCart: t('common.addToCart'),
        goToCart: t('cart.goToCart'),
        outOfStock: t('product.outOfStock'),
        increaseQuantity: t('cart.updateQuantity'),
        decreaseQuantity: t('cart.updateQuantity'),
        aboutProduct: t('product.aboutProduct'),
        sku: t('product.sku'),
        allSpecifications: t('product.allSpecifications'),
      }}
      showDescription={showDescriptionBlock}
      showAllSpecifications={showAllSpecificationsBlock}
      showWarrantyPaymentBlock={showWarrantyPaymentBlock}
      PriceActionsComponent={ProductPriceActions}
    />
  );
}

export default ProductDetail;
